/**
 * Repairing a streak lost yesterday: the callout in the web's streak popover
 * (EmailReminderAndStreakRepairView.tsx) and the iOS app's streak menu
 * (nativeHeader.ts).
 */
import * as premiumUpsellModal from "premiumUpsellModal";
import readlangClient from "readlangClient";
import * as dateUtils from "shared/dateUtils";
import stores from "stores/stores";

/**
 * A lost streak this long is worth pointing out unasked: the web's popover
 * opens itself, the app's streak shows a red dot
 */
export const minimumDaysToNudge = 4;

/** What the user can do about the streak they lost yesterday, if anything */
export type Offer =
  | { kind: "upgrade"; repairableDays: number }
  | { kind: "repair"; repairableDays: number; repairsLeft: number };

export const offer = (
  repairableDays: number | undefined
): Offer | undefined => {
  if (!repairableDays) return undefined;
  if (!stores.user.hasPremiumAccess.value) {
    return { kind: "upgrade", repairableDays };
  }
  const repairsLeft =
    (stores.user.state.value as { streakRepairs?: number }).streakRepairs ?? 0;
  return repairsLeft > 0
    ? { kind: "repair", repairableDays, repairsLeft }
    : undefined;
};

/** Repairs the streak, then reloads to show it; `onFailure` once an error's been shown */
export const repair = (onFailure?: () => void) => {
  readlangClient.request(
    "POST",
    "/api/userLearningLog/repairStreak",
    { timezoneOffset: dateUtils.getTimezoneOffset() },
    async () => {
      await stores.storedNotifications.deleteAllStoredNotifications();
      window.location.reload();
    },
    (error) => {
      onFailure?.();
      if (!error.alreadyNotified) {
        alert("Error repairing streak: " + (error.data?.error ?? error.status));
      }
    }
  );
};

export const showUpsell = () =>
  premiumUpsellModal.show({
    model: stores.user,
    title: "streakRepairUpsell",
    heading: "Go premium for streak repair and more...",
    showTitle: false,
  });
